import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFeed } from '@/hooks/useFeed';
import { useFollowList } from '@/hooks/useFollowList';
import { NoteCard } from '@/components/jp/NoteCard';
import { NoteCardSkeleton } from '@/components/jp/NoteCardSkeleton';
import { TRexSilhouette } from '@/components/jp/DinoArt';
import { Loader2 } from 'lucide-react';

export function Feed() {
  const follows = useFollowList();
  const {
    data,
    isPending,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useFeed();

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: '600px',
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const events = useMemo(() => {
    if (!data?.pages) return [];
    const seen = new Set<string>();
    const out: NonNullable<typeof data>['pages'][number]['events'] = [];
    for (const page of data.pages) {
      for (const event of page.events) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        out.push(event);
      }
    }
    return out;
  }, [data]);

  const showInitialLoading = isPending || (follows.isPending && !follows.data);

  /* ------------------------------ UI states ------------------------------ */

  if (showInitialLoading) {
    return (
      <div className="space-y-4">
        <SequencingBanner />
        {Array.from({ length: 4 }).map((_, i) => (
          <NoteCardSkeleton key={i} seed={i + 1} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="jp-panel rounded-sm p-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="jp-led-red" />
          <span className="font-mono text-jp-red tracking-widest text-sm">
            SYSTEM FAULT
          </span>
          <span className="jp-led-red" />
        </div>
        <p className="font-typewriter text-jp-bone mb-4">
          Perimeter comms lost. Retry signal acquisition.
        </p>
        <p className="font-mono text-xs text-muted-foreground mb-4">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button
          onClick={() => refetch()}
          className="font-mono text-xs px-4 py-2 border border-jp-amber text-jp-amber-bright hover:bg-jp-amber hover:text-[hsl(120_30%_6%)] transition-colors"
        >
          ▸ RE-ESTABLISH LINK
        </button>
      </div>
    );
  }

  const followsCount = follows.data?.pubkeys.length ?? 0;

  if (events.length === 0) {
    return (
      <div className="jp-panel rounded-sm px-6 py-12 text-center relative overflow-hidden">
        <TRexSilhouette className="absolute -right-6 -bottom-6 w-56 h-56 text-jp-amber/10 pointer-events-none" />
        <div className="relative">
          <div className="font-display text-2xl text-jp-amber-bright jp-electric mb-2">
            THE PARK IS QUIET
          </div>
          <p className="font-typewriter text-jp-bone/80 mb-4">
            No transmissions detected on your network.
          </p>
          <p className="font-mono text-[0.7rem] text-jp-amber/60 max-w-md mx-auto">
            {followsCount === 0
              ? 'Lens account has no follows on record yet. Check back once the network expands.'
              : `Monitoring ${followsCount} tracked specimen${followsCount === 1 ? '' : 's'}. None have transmitted recently.`}
          </p>
        </div>
      </div>
    );
  }

  /* ------------------------------ Feed list ----------------------------- */

  return (
    <div className="space-y-4">
      <FeedStatusStrip
        followsCount={followsCount}
        eventCount={events.length}
      />

      {events.map((event, i) => (
        <NoteCard key={event.id} event={event} index={i} />
      ))}

      {/* Load-more sentinel */}
      <div
        ref={sentinelRef}
        className="flex items-center justify-center py-10 font-mono text-xs text-jp-amber/70"
      >
        {isFetchingNextPage ? (
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>SCANNING DEEPER PERIMETER…</span>
          </div>
        ) : hasNextPage ? (
          <div className="flex items-center gap-2 opacity-60">
            <span className="jp-led" />
            <span>AWAITING NEXT TRANSMISSION CLUSTER</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 opacity-50">
            <span>▣ END OF LOG · ARCHIVE COMPLETE</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------- decorative bits --------------------------- */

function SequencingBanner() {
  return (
    <div className="jp-panel rounded-sm p-4">
      <div className="flex items-center gap-3 mb-2">
        <span className="jp-led" />
        <span className="font-mono text-xs tracking-[0.2em] text-jp-amber-bright">
          DNA SEQUENCING // INBOUND SIGNAL
        </span>
      </div>
      <div className="jp-dna-bar" />
      <div className="mt-1 jp-dna-bar opacity-70" />
    </div>
  );
}

function FeedStatusStrip({
  followsCount,
  eventCount,
}: {
  followsCount: number;
  eventCount: number;
}) {
  return (
    <div className="jp-panel rounded-sm px-3 py-2 flex items-center justify-between font-mono text-[0.68rem] tracking-wider text-jp-amber-bright">
      <div className="flex items-center gap-3">
        <span className="jp-led" />
        <span>MONITOR ONLINE</span>
      </div>
      <div className="hidden sm:flex items-center gap-4 opacity-80">
        <span>TRACKED: {followsCount}</span>
        <span>LOGS: {eventCount}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="hidden sm:inline">SEC CH-07</span>
        <span className="jp-led" />
      </div>
    </div>
  );
}
